import type { EscapedString } from '../c-mp/fun/html'
import type { ITreeOptions } from '../model/ITreeOptions'
import { drawTreeAncestorCouple } from './drawTreeAncestorCouple'
import { drawTreePerson } from './drawTreePerson'
import { makeTreeAncestorCouple } from './makeTreeAncestorCouple'
import { makeTreePerson } from './makeTreePerson'

const PADDING = 50
export const PERSON_WIDTH = 242
export const PERSON_PADDING_X = 5
export const LABEL_WIDTH = PERSON_WIDTH - PERSON_PADDING_X * 2
export const PERSON_HEIGHT = 150
export const IMAGE_SIZE = 64
export const IMAGE_MARGIN = 2
export const LABEL_HEIGHT = IMAGE_SIZE + IMAGE_MARGIN * 2
export const LABEL_TOP = (PERSON_HEIGHT - LABEL_HEIGHT) / 2
export const LABEL_BOTTOM = LABEL_TOP + LABEL_HEIGHT
export const LINE_HEIGHT = 18
export const LINE_HEIGHT_SMALL = 12
export const FONT_SIZE_SMALL = 12
export const LINE_RADIUS = 10
export const LABEL_RADIUS = 5
export const LABEL_SHADOW_OFFSET = 3
export const ICON_SIZE = 16
export const ICON_MARGIN_X = 4

const STYLE = `font-family: Georgia, ui-serif, serif; font-size: 16px`

export interface ISubtreeSize {
	width: number
	height: number
	x: number
}

export function makeTestSvg() {
	return `<svg
		xmlns="http://www.w3.org/2000/svg"
		width="1"
		height="1"
		style="${STYLE}"
	></svg>`
}

export async function makeSvgTree(
	test: SVGElement,
	options: ITreeOptions,
	personId: string,
) {
	const treeAncestorCouple = await makeTreeAncestorCouple(options, personId)
	const ancestorContent: string[] = []
	const ancestorSize: ISubtreeSize = treeAncestorCouple
		? drawTreeAncestorCouple({
				test,
				content: ancestorContent,
				tac: treeAncestorCouple,
				x: 0,
				y: 0,
			})
		: { width: 0, height: 0, x: 0 }

	const treePerson = await makeTreePerson(options, personId)
	const content: (EscapedString | string)[] = []
	const descendantSize = drawTreePerson({
		test,
		content,
		tp: treePerson,
		x: 0,
		y: ancestorSize.height,
		isFocused: true,
	})
	const xDiff = descendantSize.x - ancestorSize.x
	const x1 = (xDiff >= 0 ? 0 : xDiff) - PADDING
	const x2 =
		x1 +
		Math.max(
			(xDiff >= 0 ? xDiff : 0) + ancestorSize.width,
			(xDiff >= 0 ? 0 : -xDiff) + descendantSize.width,
		) +
		PADDING * 2
	const width = x2 - x1
	const height = ancestorSize.height + descendantSize.height + PADDING * 2

	const result = `<svg
		xmlns="http://www.w3.org/2000/svg"
		width="${width}"
		height="${height}"
		style="${STYLE}"
	>
		<defs>
			<clipPath id="image-clip" clipPathUnits="objectBoundingBox">
				<circle cx=".5" cy=".5" r=".5"/>
			</clipPath>
			<svg id="marriage" xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 16 16" fill="none" stroke="black"><circle cx="5" cy="8" r="4.5"/><circle cx="11" cy="8" r="4.5"/></svg>
			<svg id="divorce" xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 16 16" fill="none" stroke="black" stroke-linecap="round" stroke-linejoin="round"><circle cx="3" cy="8" r="2.5"/><circle cx="13" cy="8" r="2.5"/><path d="m8 1.5v13"/></svg>
			<svg id="infant-death" xmlns='http://www.w3.org/2000/svg' width='${IMAGE_SIZE}' height='${IMAGE_SIZE}' viewBox='0 0 16 16'><g fill='black'><path d='M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16'/><path d='M8 4a.5.5 0 0 1 .5.5v2h2a.5.5 0 0 1 0 1h-2v4a.5.5 0 0 1-1 0v-4h-2a.5.5 0 0 1 0-1h2v-2A.5.5 0 0 1 8 4'/></g></svg>
		</defs>
		<g style="transform: translate(calc(0px - var(--scroll-left-tat1pz)), calc(0px - var(--scroll-top-tat1qe))) scale(var(--scale-tat0mm))">
			<g transform="translate(${-x1},${PADDING})">
				${content.join('')}
				<g transform="translate(${xDiff},${ancestorSize.height - PERSON_HEIGHT})">${ancestorContent.join('')}</g>
			</g>
		</g>
	</svg>`
	return { svg: result, width, height }
}
